<template>
  <AppDialog
    v-model="dialog"
    title="Upload Photos"
    max-width="1100px"
    @close="close"
  >
    <v-card-text>
      <v-row class="pt-6">
        <v-col cols="12">
          <v-file-input
            label="Select Photos"
            variant="outlined"
            density="compact"
            prepend-icon="mdi-camera"
            multiple
            accept="image/*"
            @change="handleFileSelect"
          />
        </v-col>
      </v-row>
      <v-row v-if="staged.length">
        <v-col class="text-right">
          <v-btn
            size="small"
            variant="text"
            @click="fillBlankNames"
          >
            Fill Blank Names
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          v-for="(photo, index) in staged"
          :key="index"
          cols="6"
        >
          <v-card
            variant="outlined"
            class="pa-3"
          >
            <div class="photo-thumb-wrap">
              <img
                :src="photo.previewUrl"
                alt="Preview"
                class="photo-thumb"
              >
              <v-btn
                icon="mdi-close"
                size="x-small"
                class="photo-remove"
                @click="removePhoto(index)"
              />
            </div>
            <v-text-field
              v-model="photo.name"
              label="Name"
              density="compact"
              hide-details
              class="mt-2"
              :readonly="photo.locked"
              :error="!photo.name"
            >
              <template #append-inner>
                <v-icon @click="photo.locked = !photo.locked">
                  {{ photo.locked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                </v-icon>
              </template>
            </v-text-field>
            <v-text-field
              v-model="photo.imageNumber"
              label="Image Number"
              density="compact"
              hide-details
              class="mt-2"
            />
            <v-text-field
              v-model="photo.whaleId"
              label="Whale ID"
              density="compact"
              hide-details
              class="mt-2"
            />
            <v-text-field
              v-model="photo.sightingNumber"
              label="Sighting Number"
              density="compact"
              hide-details
              class="mt-2"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-progress-linear
            indeterminate
            :active="saving"
            color="primary"
          />
          <v-alert
            v-if="message && !saved"
            type="error"
          >
            {{ message }}
          </v-alert>
          <v-alert
            v-if="saved"
            type="success"
          >
            {{ message }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-btn @click="close">
        <v-icon>mdi-close</v-icon>
        Close
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="!canUpload || saved"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        Upload
      </v-btn>
    </v-card-actions>
  </AppDialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppDialog from "@/components/AppDialog";

export default {
  components: { AppDialog },
  data() {
    return {
      staged: [],
    };
  },
  computed: {
    ...mapGetters({
      saving: "images/saving",
      saved: "images/saved",
      dialog: "images/dialog",
      message: "images/message",
    }),
    canUpload() {
      return this.staged.length > 0 && this.staged.every((photo) => photo.name);
    },
  },
  watch: {
    dialog(dialog) {
      if (!dialog) {
        this.staged = [];
        this.setMessage("");
      }
    },
  },
  methods: {
    ...mapActions({
      uploadImages: "images/uploadImages",
      close: "images/close",
      setMessage: "images/setMessage",
    }),
    parseImageNumber(filename) {
      const match = filename.match(/^IMG(\d+)/i);
      return match ? String(parseInt(match[1], 10)) : "";
    },
    handleFileSelect(event) {
      const files = event.target.files;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        reader.onload = (e) => {
          this.staged.push({
            file: file,
            previewUrl: e.target.result,
            name: file.name,
            imageNumber: this.parseImageNumber(file.name),
            whaleId: "",
            sightingNumber: "",
            locked: false,
          });
        };
        reader.readAsDataURL(file);
      }
    },
    removePhoto(index) {
      this.staged.splice(index, 1);
    },
    fillBlankNames() {
      let counter = 1;
      this.staged.forEach((photo) => {
        if (!photo.name) {
          photo.name = `UNNAMED_FILE_${counter}`;
          counter += 1;
        }
      });
    },
    async save() {
      const photos = this.staged.map((photo) => ({
        file: photo.file,
        name: photo.name,
        imageNumber: photo.imageNumber,
        whaleId: photo.whaleId || "na",
        sightingNumber: photo.sightingNumber || "na",
      }));
      await this.uploadImages({ photos });
    },
  },
};
</script>

<style scoped>


.photo-thumb-wrap {
  position: relative;
}

.photo-thumb {
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 4px;
}

.photo-remove {
  position: absolute;
  top: 4px;
  right: 4px;
}

</style>
